import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import {
  useAttackPackageTemplates,
  useAttackPlans,
  useCreateAttackPlan,
  useDeleteAttackPlan,
  useUpdateAttackPlan,
  useWorldOptions,
} from '../api/queries';
import { ATTACK_PLAN_STATUS_LABELS, type AttackPlan, type AttackPlanMode, type AttackPlanStatus, type User } from '../api/types';
import { useToast } from '../components/Toast';

interface FormState {
  name: string;
  mode: AttackPlanMode;
  wavesPerTarget: string;
  fakePackageTemplateId: string;
  fakeArrivalStart: string;
  fakeArrivalEnd: string;
}

function emptyForm(): FormState {
  return { name: '', mode: 'manual', wavesPerTarget: '', fakePackageTemplateId: '', fakeArrivalStart: '', fakeArrivalEnd: '' };
}

function fromDatetimeLocal(value: string): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function formatCoverage(percent: number | null): string {
  return percent === null ? '–' : `${Math.round(percent)}%`;
}

/**
 * Übersicht aller Kriegspläne: Name, Abdeckung (aus dem zuletzt
 * gespeicherten Prüfergebnis, kein Neu-Rechnen beim Auflisten) und ein
 * manuell gepflegter Arbeitsstatus.
 */
export function AttackPlansPage({ user }: { user: User }) {
  // In der URL statt in reinem Komponenten-State -- ein Reload oder ein
  // Wechsel zu den Pakettyp-Vorlagen und zurück soll die gewählte Welt
  // nicht verlieren (explizite Nutzeranfrage, gleiches Muster wie
  // AdminUsersPage.tsx).
  const [searchParams, setSearchParams] = useSearchParams();
  const needsWorldChoice = user.world === null;
  const worldKey = searchParams.get('welt') ?? '';
  const worlds = useWorldOptions('login');
  const activeWorld = user.world?.key ?? worldKey;

  const plans = useAttackPlans(activeWorld || undefined);
  const packageTemplates = useAttackPackageTemplates(activeWorld || undefined);
  const create = useCreateAttackPlan(activeWorld || undefined);
  const remove = useDeleteAttackPlan(activeWorld || undefined);
  const toast = useToast();

  const [form, setForm] = useState<FormState>(emptyForm());

  const rows = [...(plans.data?.plans ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const templates = packageTemplates.data?.templates ?? [];

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (form.mode === 'katta' && (!form.wavesPerTarget || !form.fakePackageTemplateId)) {
      toast.push('error', 'Bitte Wellenzahl je Ziel und Fake-Pakettyp angeben.');
      return;
    }
    create.mutate(
      {
        name: form.name,
        mode: form.mode,
        wavesPerTarget: form.mode === 'katta' ? Number(form.wavesPerTarget) : null,
        fakePackageTemplateId: form.mode === 'katta' ? Number(form.fakePackageTemplateId) : null,
        fakeArrivalStart: form.mode === 'katta' ? fromDatetimeLocal(form.fakeArrivalStart) : null,
        fakeArrivalEnd: form.mode === 'katta' ? fromDatetimeLocal(form.fakeArrivalEnd) : null,
      },
      {
        onSuccess: () => {
          toast.push('success', 'Kriegsplan wurde erstellt.');
          setForm(emptyForm());
        },
        onError: (err) => toast.push('error', err.message),
      },
    );
  };

  const confirmDelete = (plan: AttackPlan) => {
    if (!window.confirm(`Kriegsplan "${plan.name}" löschen?`)) return;
    remove.mutate(
      { id: plan.id },
      {
        onSuccess: () => toast.push('success', 'Kriegsplan wurde gelöscht.'),
        onError: (err) => toast.push('error', err.message),
      },
    );
  };

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h1>Angriffsplaner</h1>
          <p className="muted">
            Reiner Rechner: prüft, ob der hinterlegte Truppenpool ausreicht, um alle Zieldörfer eines Plans rechtzeitig
            mit den gewünschten Wellen zu treffen. Sendet nie etwas selbst.
          </p>
        </div>
        <div className="button-row">
          <Link to={`/angriffsplaner/pakettypen${worldKey ? `?welt=${worldKey}` : ''}`} className="btn btn-sm">
            Pakettyp-Vorlagen
          </Link>
          <Link to={`/angriffsplaner/workbench-gruppieren${worldKey ? `?welt=${worldKey}` : ''}`} className="btn btn-sm">
            Workbench gruppieren
          </Link>
        </div>
      </div>

      {needsWorldChoice && (
        <label className="field">
          <span>Welt</span>
          <select value={worldKey} onChange={(event) => setSearchParams({ welt: event.target.value }, { replace: true })} required>
            <option value="" disabled>
              Welt wählen …
            </option>
            {(worlds.data?.worlds ?? []).map((world) => (
              <option key={world.key} value={world.key}>
                {world.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {needsWorldChoice && !worldKey && <p className="muted center pad">Wähle eine Welt, um ihre Kriegspläne zu sehen.</p>}

      {activeWorld && (
        <>
          {create.error instanceof ApiError && <div className="flash flash-error">{create.error.message}</div>}

          <form className="form" onSubmit={handleSubmit} noValidate>
            <div className="field-row">
              <label className="field grow">
                <span>Name</span>
                <input type="text" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
              </label>
              <label className="field">
                <span>Modus</span>
                <select value={form.mode} onChange={(e) => setForm((f) => ({ ...f, mode: e.target.value as AttackPlanMode }))}>
                  <option value="manual">Manuell</option>
                  <option value="katta">Katta (mit Fake-Auffüllung)</option>
                </select>
              </label>
            </div>

            {form.mode === 'katta' && (
              <div className="field-row">
                <label className="field">
                  <span>Wellen je Zieldorf (insgesamt, inkl. Fakes)</span>
                  <input
                    type="number"
                    min={1}
                    value={form.wavesPerTarget}
                    onChange={(e) => setForm((f) => ({ ...f, wavesPerTarget: e.target.value }))}
                    required
                  />
                </label>
                <label className="field grow">
                  <span>Pakettyp für Fakes</span>
                  <select
                    value={form.fakePackageTemplateId}
                    onChange={(e) => setForm((f) => ({ ...f, fakePackageTemplateId: e.target.value }))}
                    required
                  >
                    <option value="" disabled>
                      Pakettyp wählen …
                    </option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}

            {form.mode === 'katta' && (
              <div className="field-row">
                <label className="field">
                  <span>Fake-Ankunft ab (optional)</span>
                  <input
                    type="datetime-local"
                    step="1"
                    value={form.fakeArrivalStart}
                    onChange={(e) => setForm((f) => ({ ...f, fakeArrivalStart: e.target.value }))}
                  />
                </label>
                <label className="field">
                  <span>Fake-Ankunft bis (optional)</span>
                  <input
                    type="datetime-local"
                    step="1"
                    value={form.fakeArrivalEnd}
                    onChange={(e) => setForm((f) => ({ ...f, fakeArrivalEnd: e.target.value }))}
                  />
                </label>
                <span className="hint">Zeitspanne, über die die auffüllenden Fake-Wellen gestreut werden -- ohne Angabe übernehmen sie die Ankunftszeiten der echten Wellen.</span>
              </div>
            )}

            <div className="button-row">
              <button type="submit" className="btn btn-primary" disabled={create.isPending}>
                {create.isPending ? 'Wird erstellt …' : 'Kriegsplan erstellen'}
              </button>
            </div>
          </form>

          {plans.isPending && <p className="muted">Wird geladen …</p>}
          {!plans.isPending && rows.length === 0 && <p className="muted center pad">Noch keine Kriegspläne angelegt.</p>}

          {rows.length > 0 && (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Modus</th>
                    <th>Abdeckung</th>
                    <th>Status</th>
                    <th className="right">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((plan) => (
                    <PlanRow key={plan.id} plan={plan} worldKey={activeWorld} onDelete={() => confirmDelete(plan)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function PlanRow({ plan, worldKey, onDelete }: { plan: AttackPlan; worldKey: string; onDelete: () => void }) {
  const update = useUpdateAttackPlan(worldKey);
  const toast = useToast();

  const changeStatus = (status: AttackPlanStatus) => {
    update.mutate({ id: plan.id, status }, { onError: (err) => toast.push('error', err.message) });
  };

  return (
    <tr>
      <td>
        <Link to={`/angriffsplaner/plaene/${plan.id}${worldKey ? `?welt=${worldKey}` : ''}`}>{plan.name}</Link>
      </td>
      <td>{plan.mode === 'katta' ? 'Katta' : 'Manuell'}</td>
      <td className="nowrap">{formatCoverage(plan.lastCheckCoveragePercent)}</td>
      <td>
        <select value={plan.status} disabled={update.isPending} onChange={(e) => changeStatus(e.target.value as AttackPlanStatus)}>
          {Object.entries(ATTACK_PLAN_STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </td>
      <td className="right">
        <button type="button" className="btn btn-danger btn-sm" onClick={onDelete}>
          Löschen
        </button>
      </td>
    </tr>
  );
}
